import { Breadcrumbs } from '@bridebook/web/app-basic/components/breadcrumbs/breadcrumbs-supplier.container';
import { AutocompleteInputLocation } from '@bridebook/web/app-basic/components/search/location-autocomplete/autocomplete-input-location';
import GooglePredictionsContextProvider from '@bridebook/web/app-basic/components/search/location-autocomplete/context';
import { AvailableTranslations } from '@bridebook/web/app-basic/components/supplier/sections/available-translations';
import { Gallery } from '@bridebook/web/app-basic/components/supplier/sections/gallery/gallery';
import CarouselGridGallery from '@bridebook/web/app-basic/components/supplier/sections/carousel-grid-gallery/carousel-grid-gallery';
import { InstagramWidget } from '@bridebook/web/app-basic/components/supplier/sections/instagram-widget';
import { Social } from '@bridebook/web/app-basic/components/supplier/sections/social';
import StickyBrochureRequest from '@bridebook/web/app-basic/components/supplier/sections/sticky-brochure-request/sticky-brochure-request';
import { StickyTabs } from '@bridebook/web/app-basic/components/supplier/sections/sticky-tabs';
import { viewedSupplierProfileOnLoggedOutPage } from '@bridebook/web/app-basic/lib/analytics/tracking-actions/viewed-supplier-media';
import {
  getIsCordova,
  getIsDesktop,
  getIsMobile,
} from '@bridebook/web/app-basic/lib/app/selectors';
import useLoggedOutSearchBar from '@bridebook/web/app-basic/lib/hooks/use-logged-out-search-bar';
import { useSupplierBucketSetting } from '@bridebook/web/app-basic/lib/hooks/use-supplier-bucket-setting';
import {
  getIsPhotographer,
  getIsVenue,
  getPremium,
  getSupplier,
  getSupplierCountry,
  getSupplierLoaded,
  getSupplierTier,
  isSupplierRegistered,
} from '@bridebook/web/app-basic/lib/supplier/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { StickyContainer } from 'react-sticky';
import { supplierListingPropertiesGeneric } from '@bridebook/toolbox/src/analytics-utils';
import { CountryCodes, Gazetteer } from '@bridebook/toolbox/src/gazetteer';
import { Box, Divider, LoadingCircle } from '@bridebook/ui';
import { IconSearch } from '@bridebook/ui/src/icons';
import AboveRequestButton from '@bridebook/web/app-shared/components/supplier/sections/above-request-button/above-request-button';
import { AccordionSections } from '@bridebook/web/app-shared/components/supplier/sections/accordion-sections/accordion-sections';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { useGetViewedLocation } from '@bridebook/web/app-shared/lib/supplier/hooks/use-get-viewed-location';
import { fullDescriptionVisibility } from '@bridebook/web/app-shared/lib/supplier/utils/fullDescriptionVisibility';
import { getIsSupplierPreview } from '@bridebook/web/app-shared/lib/supplier/utils/get-is-supplier-preview';
import { IBreadcrumbItem } from '@bridebook/web/app-shared/lib/types';
import { useScrollToTopOnRouteChange } from '@bridebook/web/app-shared/lib/ui/hooks/use-scroll-to-top-on-route-change';
import componentStyles from './profile-container.style';
import AboutMe from './sections/about-me';
import Awards from './sections/awards';
import Benefits from './sections/benefits';
import DescriptionShort from './sections/description-short';
import Faq from './sections/faq';
import LocationContactDetails from './sections/location-contact-details';
import MediaButtonSection from './sections/media-button-section';
import Pricing from './sections/pricing';
import ProfileHighlights from './sections/profile-highlights';
import RecommendedPhotographer from './sections/recommended-photographer';
import RelatedLocationsContainer from './sections/related-locations/related-locations-container';
import Reviews from './sections/reviews';
import SpecialOffers from './sections/special-offers/special-offers';
import SupplierInfo from './sections/supplier-info';
import SupplierKeyInfo from './sections/supplier-key-info';
import WeddingFairs from './sections/wedding-fairs';
import YourBusiness from './sections/your-business/your-business';
import StickyBrochureRequestCard from './sections/sticky-brochure-request/sticky-brochure-request-card';
import VenueReviews from '@bridebook/web/app-basic/components/supplier/sections/reviews/venue-reviews';
import { useIsVenueProfileV2 } from './hooks/use-is-venue-profile-v2';
import Highlights from './sections/highlights/highlights';
import DescriptionFull from '@bridebook/web/app-basic/components/supplier/sections/description-full/description-full';
import Features from '@bridebook/web/app-basic/components/supplier/sections/features/features';
import ToggleEnquiryFormWithSignup from './toggle-enquiry-form-with-signup';
import SimilarVenuesCarousel from '@bridebook/web/app-basic/components/supplier/sections/similar-venues-carousel/similar-venues-carousel';
import { CTAButtonsWrapper } from './sections/cta-buttons/cta-buttons-wrapper';
import { Flex } from '@bridebook/aura';
import { useABTest_ShowroundsLoggedOutUsers } from '@bridebook/web/app-basic/lib/ab-tests/tests/LIVE-28500_ShowroundsLoggedOutUsers';

const ProfileContainer = ({ breadcrumbsData }: { breadcrumbsData: IBreadcrumbItem[] }) => {
  const { t } = useTranslation('supplier');
  const market = useMarket();
  const dispatch = useDispatch();
  const router = useRouter();
  const viewedLocation = useGetViewedLocation();
  const isMobile = useSelector(getIsMobile);
  const isCordova = useSelector(getIsCordova);
  useScrollToTopOnRouteChange(isCordova);
  const isDesktop = useSelector(getIsDesktop);
  const supplier = useSelector(getSupplier);
  const supplierRegistered = useSelector(isSupplierRegistered);
  const supplierTier = useSelector(getSupplierTier);
  const premium = useSelector(getPremium);
  const isVenue = useSelector(getIsVenue);
  const loaded = useSelector(getSupplierLoaded);
  const supplierCountry = useSelector(getSupplierCountry);
  const isPhotographer = useSelector(getIsPhotographer);
  const { isEnabled: isProfileHighlightsSectionEnabled } = useSupplierBucketSetting(
    'profileHighlightsSection',
    supplier?.type[0],
  );
  const {
    showIcon,
    onPlaceSelected,
    handleOnFocus,
    searchBarEnabled,
    loading: searchBarRedirectLoading,
    handleOnBlur,
  } = useLoggedOutSearchBar(
    'profilePageSearchBar',
    !!(supplier && supplier.type.includes('venue')),
  );
  const isSupplierPreview = getIsSupplierPreview(router.query);
  const showTopBreadcrumb = !isSupplierPreview;
  const isVenueProfileV2 = useIsVenueProfileV2();

  useEffect(() => {
    if (!supplierCountry && !viewedLocation) return;
    const supplierProperties = supplier ? supplierListingPropertiesGeneric(supplier) : undefined;
    dispatch(
      viewedSupplierProfileOnLoggedOutPage({
        supplierCountryCode: supplierCountry,
        ...(viewedLocation &&
          supplier &&
          supplierProperties && {
            viewedLocation,
            supplierRegistered,
            supplierTier,
            supplierCategory: supplier.type[0],
            adminArea0: supplier.address.adminArea[0],
            adminArea1: supplier.address.adminArea[1],
            supplierId: supplier.id,
            supplierCountryCode: supplier.l10n.country,
            supplierCountryName: Gazetteer.getCountryName(supplier.l10n.country),
            supplierName: supplier.name,
            supplierProfileURL: supplierProperties.supplierProfileURL,
          }),
      }),
    );
  }, [
    dispatch,
    viewedLocation,
    supplierCountry,
    supplier,
    premium,
    supplierRegistered,
    supplierTier,
  ]);

  useABTest_ShowroundsLoggedOutUsers();

  if (!supplier) return null;

  const styles = componentStyles({ isMobile, isDesktop });

  return (
    <StickyContainer>
      <Box style={styles.wrapper(isVenueProfileV2)}>
        {isDesktop && !isVenueProfileV2 && (
          <Box style={styles.availableTranslations(false)}>
            <AvailableTranslations />
          </Box>
        )}
        {loaded ? (
          <>
            {!isVenueProfileV2 && (
              <>
                {showTopBreadcrumb && isDesktop && (
                  <Breadcrumbs breadcrumbsData={breadcrumbsData} />
                )}
                <Box style={styles.searchBarWrapper}>
                  {searchBarEnabled && (
                    <GooglePredictionsContextProvider>
                      <AutocompleteInputLocation
                        name="address-auto-suggest"
                        onPlaceChanged={onPlaceSelected}
                        onFocusProp={handleOnFocus}
                        label={t('loggedOutSearchBar:autocomplete.modalLabel')}
                        onBlurProp={handleOnBlur}
                        placeholder={t('loggedOutSearchBar:autocomplete.placeholder')}
                        disabled={searchBarRedirectLoading}
                        afterInput={
                          showIcon ? (
                            <Box style={styles.searchBarIcon(searchBarRedirectLoading)}>
                              {searchBarRedirectLoading ? (
                                <LoadingCircle size={18} color="white" center />
                              ) : (
                                <IconSearch width={16} color="white" p={2} />
                              )}
                            </Box>
                          ) : undefined
                        }
                      />
                    </GooglePredictionsContextProvider>
                  )}
                </Box>
                <Divider spacingTop={5} spacingBottom={isDesktop ? 5 : 0} />
              </>
            )}
            {!isDesktop && showTopBreadcrumb && (
              <Box px="6px">
                <Breadcrumbs breadcrumbsData={breadcrumbsData} />{' '}
              </Box>
            )}
            {!isVenueProfileV2 && (
              <>
                <Box style={styles.infoWrap(false)}>
                  <SupplierInfo breadcrumbsData={breadcrumbsData} />
                </Box>
                {supplier && isVenue && (
                  <Box style={styles.keyInfoWrap}>
                    <SupplierKeyInfo />
                  </Box>
                )}
              </>
            )}
            <Box style={styles.carouselWrap}>
              {isVenueProfileV2 ? (
                <ToggleEnquiryFormWithSignup genericTitle>
                  <CarouselGridGallery />
                </ToggleEnquiryFormWithSignup>
              ) : (
                <Gallery />
              )}
            </Box>
            {isVenueProfileV2 && (
              <Box style={styles.infoWrap(true)}>
                <SupplierInfo isVenueProfileV2 breadcrumbsData={breadcrumbsData} />
                <Box style={styles.availableTranslations(true)}>
                  <AvailableTranslations />
                </Box>
              </Box>
            )}
            {!isVenueProfileV2 && (
              <Box style={styles.mediaButtonSectionWrap}>
                <MediaButtonSection />
                {!isDesktop && (
                  <Box style={styles.availableTranslations(false)}>
                    <AvailableTranslations />
                  </Box>
                )}
              </Box>
            )}
            <CTAButtonsWrapper />
            {isProfileHighlightsSectionEnabled && isVenueProfileV2 && !isDesktop && <Highlights />}
            {isProfileHighlightsSectionEnabled && isPhotographer && <ProfileHighlights />}
            {!isDesktop && <StickyBrochureRequest />}
          </>
        ) : (
          <Box style={styles.loadingWrap}>
            <LoadingCircle size={50} />
          </Box>
        )}
        <Box style={styles.content(isVenueProfileV2)}>
          {!isVenue && (supplier?.about?.name ? <AboutMe /> : <DescriptionShort />)}
          <YourBusiness />
          <StickyContainer>
            {isVenue ? (
              isVenueProfileV2 ? (
                <Flex className="flex-col xl:flex-row xl:gap-6">
                  <Box style={styles.stickyBox(true)}>
                    {isVenueProfileV2 && isDesktop && <Highlights />}
                    <StickyTabs />
                    {premium && <SpecialOffers />}
                    {fullDescriptionVisibility(supplier) ? (
                      <DescriptionFull />
                    ) : (
                      <DescriptionShort />
                    )}
                    <Features />
                    <Pricing />
                    <VenueReviews />
                    <LocationContactDetails />
                    <InstagramWidget />
                    <Social />
                    <WeddingFairs />
                  </Box>
                  {isDesktop && <StickyBrochureRequestCard />}
                </Flex>
              ) : (
                <>
                  <StickyTabs />
                  {premium && <SpecialOffers />}
                  {fullDescriptionVisibility(supplier) ? <DescriptionFull /> : <DescriptionShort />}
                  <Features />
                  <Pricing />
                  <Reviews />
                  <LocationContactDetails />
                  <InstagramWidget />
                  <Social />
                  <WeddingFairs />
                </>
              )
            ) : (
              <>
                <Pricing />
                <InstagramWidget />
                <Features />
                <DescriptionFull />
                <Reviews />
                <WeddingFairs />
                <LocationContactDetails />
              </>
            )}
          </StickyContainer>
          <AccordionSections>
            <Faq />
            <Awards />
          </AccordionSections>
          {loaded && isVenue && market.country === CountryCodes.GB && <RecommendedPhotographer />}
        </Box>
        <Box style={styles.bottomContent}>
          <Benefits />
          <AboveRequestButton>
            <Box style={styles.topBorder}>
              <RelatedLocationsContainer breadcrumbsData={breadcrumbsData} />
            </Box>
          </AboveRequestButton>
        </Box>
        {isVenueProfileV2 && (
          <Box style={styles.similarVenuesCarousel}>
            <SimilarVenuesCarousel />
          </Box>
        )}
      </Box>
    </StickyContainer>
  );
};

export default ProfileContainer;
